import React, { useCallback, useState } from "react";
import { commitMutation, graphql } from "react-relay";

import { faCheck } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import moment from "moment";

import UserSelect, { type DropdownOption } from "~/common/UserSelect";
import { environment } from "~/models";
import { Button } from "~/ui";
import ArrowDropdown, { DropdownItem } from "~/ui/ArrowDropdown";
import Avatar from "~/ui/legacy/Avatar";
import type { UserAttrs } from "~/ui/legacy/Avatar";
import { HelpCenterLink, Modal } from "~/ui/legacyFellowIndex";
import toast from "~/ui/toast";

import { ExpirySelect } from "./utils";

import type { AddDelegateModalMutation as AddDelegateModalMutationType } from "~/__generated__/AddDelegateModalMutation.graphql";

import styles from "./css/AddDelegateModal.module.css";
import modalStyles from "./css/ModalStyles.module.css";

const AddDelegateModalMutation = graphql`
	mutation AddDelegateModalMutation($userId: ID!, $expiry: DateTime!) {
		createDelegate(input: { userId: $userId, expiry: $expiry }) {
			ok
			delegate {
				id
				expiryDate
				lastAccessed
				createdAt
				delegate {
					id
					fullName
					email
					image
				}
			}
		}
	}
`;

export const EXPIRY_FILTER_VALUES = ["No expiry date", "1 Day", "1 Week", "1 Month", "1 Year", "Custom"] as const;
export type ExpiryFilterValuesType = (typeof EXPIRY_FILTER_VALUES)[number];

type AddDelegateModalProps = {
	excludeUsers: string[];
	onClose: () => void;
};

interface MomentOptions {
	amount: moment.DurationInputArg1;
	unitOfTime: moment.unitOfTime.DurationConstructor | undefined;
}

export default function AddDelegateModal({ onClose, excludeUsers }: AddDelegateModalProps) {
	const [step, setStep] = useState<"select" | "confirm">("select");
	const [userInfo, setUserInfo] = useState<UserAttrs>();
	const [expiryFilter, setExpiryFilter] = useState<ExpiryFilterValuesType>(EXPIRY_FILTER_VALUES[0]);
	const [expiryDate, setExpiryDate] = useState<moment.Moment | null>();
	const [showDatePicker, setShowDatePicker] = useState(false);

	const zone = expiryDate?.toDate().toLocaleTimeString("en-us", { timeZoneName: "short" }).split(" ")[2];

	const closeDatePicker = useCallback(() => {
		setShowDatePicker(false);
	}, [setShowDatePicker]);

	const expiryFilterValuesAndSetters = React.useMemo(
		() =>
			EXPIRY_FILTER_VALUES.map(value => ({
				filterValue: value,
				setExpiryFilter: setExpiryFilter.bind(null, value),
			})),
		[],
	);

	const onNext = useCallback(() => {
		setStep("confirm");
	}, []);

	const onBack = useCallback(() => {
		setStep("select");
	}, []);

	const onConfirmClick = useCallback(() => {
		if (expiryDate !== undefined && userInfo) {
			commitMutation<AddDelegateModalMutationType>(environment, {
				mutation: AddDelegateModalMutation,
				variables: {
					userId: userInfo.id,
					expiry: expiryDate ? expiryDate.format() : moment().add(3000, "years").format(),
				},
				onCompleted: data => {
					if (data.createDelegate?.ok) {
						toast.success(`${userInfo.fullName} (${userInfo.email}) now has full access to your account.`);
					}
					onClose();
				},
				updater: store => {
					const delegate = store.getRootField("createDelegate").getLinkedRecord("delegate");

					if (!delegate) {
						return;
					}

					const viewer = store.get("viewer");
					const delegates = viewer?.getLinkedRecords("delegates");

					if (delegates) {
						viewer?.setLinkedRecords([...delegates, delegate], "delegates");
					}
				},
			});
		}
	}, [userInfo, expiryDate, onClose]);

	const updateUserFilter = useCallback((option: DropdownOption | DropdownOption[] | null) => {
		if (option && !Array.isArray(option)) {
			if (option.user) {
				setUserInfo(option.user);
			}
		}
	}, []);

	React.useEffect(() => {
		let expiryDate;

		// default for custom and 1 day expiry option
		let momentSettings: MomentOptions = {
			amount: 1,
			unitOfTime: "days",
		};

		switch (expiryFilter) {
			case "Custom":
				setShowDatePicker(true);
				break;
			case "1 Day":
				break;
			case "1 Week":
				momentSettings.unitOfTime = "weeks";
				break;
			case "1 Month":
				momentSettings.unitOfTime = "months";
				break;
			case "1 Year":
				momentSettings.unitOfTime = "years";
				break;
			default:
				momentSettings.amount = 3000;
				momentSettings.unitOfTime = "years";
		}

		expiryDate = moment().add(momentSettings.amount, momentSettings.unitOfTime).endOf("day");

		setExpiryDate(expiryFilter === "No expiry date" ? null : expiryDate);
	}, [expiryFilter]);

	const onDateChange = useCallback(
		(newDate: moment.Moment) => {
			setExpiryDate(newDate.endOf("day"));
			setShowDatePicker(false);
		},
		[setShowDatePicker, setExpiryDate],
	);

	const onNoExpiry = useCallback(() => {
		setExpiryDate(null);
		setShowDatePicker(false);
		setExpiryFilter("No expiry date");
	}, [setShowDatePicker, setExpiryDate]);

	const editDate = useCallback(() => {
		if (showDatePicker === false) setShowDatePicker(true);
	}, [showDatePicker, setShowDatePicker]);

	function renderAddStep() {
		return (
			<>
				<Modal.Header onClose={onClose} width="37.25rem">
					<Modal.Title>Add a delegate</Modal.Title>
				</Modal.Header>
				<Modal.Body className={styles.modalBody}>
					<div className={styles.bodyHeader}>
						Give a teammate full access to your account. They cannot add and edit delegates on your behalf.
					</div>

					<div className={cx(styles.label)}>Delegate user</div>

					<div className={styles.inputContainer}>
						<UserSelect
							exclude={excludeUsers}
							value={userInfo?.id}
							onChange={updateUserFilter}
							placeholder="Delegate user"
							filterGuests={true}
							hasPlaceholderIcon={true}
							className={cx(styles.userSelect)}
						/>
					</div>

					<div className={cx(styles.label)}>Expiry date</div>
					<div className={styles.inputContainer}>
						{expiryFilter !== EXPIRY_FILTER_VALUES[0] && expiryDate ? (
							<>
								<div className={modalStyles.datePickerContainer} onClick={editDate}>
									<div className={modalStyles.text}>{expiryDate.format("ll")}</div>
									<div className={modalStyles.editDate}>Edit expiry date</div>
								</div>
								{showDatePicker && (
									<ExpirySelect
										date={expiryDate ? expiryDate : undefined}
										updateDate={onDateChange}
										removeDate={onNoExpiry}
										onClose={closeDatePicker}
									/>
								)}
							</>
						) : (
							<ArrowDropdown className={styles.expiryDropdown} heading="Expiry date" text={"No expiry date"}>
								{expiryFilterValuesAndSetters.map(({ filterValue, setExpiryFilter }) => {
									return (
										<DropdownItem key={filterValue} onClick={setExpiryFilter} className={styles.dropdownItem}>
											{filterValue}
											{filterValue === expiryFilter && (
												<FontAwesomeIcon icon={faCheck} fixedWidth className={styles.checkIcon} />
											)}
										</DropdownItem>
									);
								})}
							</ArrowDropdown>
						)}
						<div className={styles.inputNote}>Delegate’s access will expire at midnight of the set expiry date.</div>
					</div>
				</Modal.Body>
			</>
		);
	}

	function renderConfirmStep() {
		return (
			<>
				<Modal.Header onClose={onClose}>
					<Modal.Title>Confirm user account delegation</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className={modalStyles.bodyHeading}>
						Please confirm that you wish to grant access to your account to the following user:
					</div>

					<div className={modalStyles.infoBox}>
						<div className={modalStyles.infoRow}>
							<div className={modalStyles.title}>Delegate:</div>
							<div className={modalStyles.info}>
								<div className={modalStyles.subject}>
									<Avatar user={userInfo} width="1.5em" link={false} />
									<span className={modalStyles.name}>{userInfo?.fullName}</span>
								</div>
							</div>
						</div>

						{}
						<div className={modalStyles.infoRow}>
							<div className={modalStyles.title}>Expiry date:</div>
							<div className={modalStyles.info}>{expiryDate ? expiryDate?.format("ll") : "No Expiry"}</div>
						</div>

						{expiryDate && (
							<div className={modalStyles.infoRow}>
								<div className={modalStyles.title}>Time:</div>
								<div className={modalStyles.info}>{`${expiryDate.format("h:mm:ss A")} ${zone}`}</div>
							</div>
						)}
					</div>

					<div className={modalStyles.bodyHeading}>
						By delegating access to your workspace, this user will have <strong>full access</strong> to your account on
						your behalf (i.e. they can “impersonate” you).{" "}
						<HelpCenterLink articleId="5887349">Learn more about Delegated access</HelpCenterLink>.
					</div>
				</Modal.Body>
			</>
		);
	}

	let content = renderAddStep;
	if (step === "confirm") content = renderConfirmStep;
	if (step === "select") content = renderAddStep;

	return (
		<Modal onClose={onClose}>
			{content()}
			<Modal.Footer>
				{step == "select" ? (
					<>
						<Button size="normal" onClick={onClose}>
							Cancel
						</Button>
						<Button
							size="normal"
							variant="primary"
							disabled={expiryDate === undefined || userInfo === undefined}
							onClick={onNext}
						>
							Next
						</Button>
					</>
				) : (
					<>
						<Button size="normal" onClick={onBack}>
							Back
						</Button>
						<Button size="normal" variant="primary" onClick={onConfirmClick}>
							Confirm
						</Button>
					</>
				)}
			</Modal.Footer>
		</Modal>
	);
}
